import React, { Suspense } from 'react';
import { initReactI18next } from 'react-i18next';
import environment from '@ktag/styleguide/src/javascripts/environment/environment';
import {
  Contentheader,
  Header,
  Footer,
  Pagesection,
  PageWrapper,
  Relatednav,
  WebWrapper,
} from '@ktag/styleguide/dist/react';
import analytics from '@ktag/styleguide/src/javascripts/analytics/analytics';
import { i18n } from '@ktag/styleguide/dist/base';
import Stylesheet from 'components/stylesheet/Stylesheet';
import Swisscoverysearch, {
  Placeholder,
} from './components/swisscoverysearch/react/Swisscoverysearch';
import environmentConfig from './config/environment';

const RELATEDNAV_ENTRIES = [
  {
    linkText: 'Ausleihe und Benutzung',
    target: {
      url: '#',
    },
  },
  {
    linkText: 'Recherche und Forschung',
    target: {
      url: '#',
    },
  },
  {
    linkText: 'Veranstaltungen und Schulungen',
    target: {
      url: '#',
    },
  },
  {
    linkText: 'Sammlungen und Bestände',
    target: {
      url: '#',
    },
  },
  {
    linkText: 'Newsletter',
    target: {
      url: '#',
    },
  },
  {
    linkText: 'Über uns',
    target: {
      url: '#',
    },
  },
  {
    linkText: 'Aargauer Bibliotheksnetz',
    target: {
      url: '#',
    },
  },
];

const FOOTER_CONFIG = {
  label: 'Footer',
  config: {
    headline: 'Bibliothek und Archiv Aargau',
    contact: {
      name: null,
      department: 'Departement Bildung, Kultur und Sport',
      role: 'Bibliothek und Archiv Aargau',
      division: 'Kantonsbibliothek',
      title: null,
      further: null,
      firstname: null,
      lastname: null,
      address: 'Obere Vorstadt 6',
      zip: '5000',
      locality: 'Aarau',
    },
    contactlinks: [
      {
        title: 'Telefon: +41 (0)62 835 23 62',
        text: 'Telefon: +41 (0)62 835 23 62',
        url: 'tel:+41628352362',
        type: 'tel',
      },
      {
        title: 'kantonsbibliothek@ag.ch',
        text: 'kantonsbibliothek@ag.ch',
        url: 'mailto:kantonsbibliothek@ag.ch',
        type: 'email',
      },
      {
        title: 'www.ag.ch/kantonsbibliothek',
        text: 'www.ag.ch/kantonsbibliothek',
        url: 'https://www.ag.ch/kantonsbibliothek',
        type: 'website',
      },
      {
        title: 'Newsletter Kantonsbibliothek',
        text: 'Newsletter Kantonsbibliothek',
        url: 'https://www.ag.ch/de/verwaltung/bks/kultur/kulturpflege/bibliothek-archiv/kantonsbibliothek/newsletter',
        type: 'newsletter',
      },
    ],
    sociallinks: [
      {
        title: 'Facebook',
        text: 'Facebook',
        url: 'https://www.facebook.com/AargauerKantonsbibliothek',
        type: 'facebook',
      },
      {
        title: 'Instagram',
        text: 'Instagram',
        url: 'https://www.instagram.com/kulturkantonaargau',
        type: 'instagram',
      },
    ],
    map: {
      address: 'Obere Vorstadt 6',
      zip: '5000 ',
      locality: 'Aarau',
    },
  },
  imprintLinks: [
    {
      title: 'Kontakt',
      url: 'https://www.ag.ch/de/ueber-uns/kontakt',
    },
    {
      title: 'Impressum',
      url: 'https://www.ag.ch/de/ueber-uns/impressum',
    },
    {
      title: 'Rechtliche Hinweise',
      url: 'https://www.ag.ch/de/ueber-uns/rechtliche-hinweise',
    },
    {
      title: 'Barrierefreiheit',
      url: 'https://www.ag.ch/de/ueber-uns/barrierefreiheit',
    },
  ],
  metaLinks: [
    {
      title: 'Jobs & Karriere',
      text: 'Jobs & Karriere',
      url: 'https://www.ag.ch/de/ueber-uns/jobs-karriere',
    },
    {
      title: 'Medien',
      text: 'Medien',
      url: 'https://www.ag.ch/de/aktuell/medien',
    },
    {
      title: 'Aktuell',
      text: 'Aktuell',
      url: 'https://www.ag.ch/de/aktuell',
    },
    {
      title: 'Themen',
      text: 'Themen',
      url: 'https://www.ag.ch/de/themen',
    },
    {
      title: 'Dienstleistungen',
      text: 'Dienstleistungen',
      url: 'https://www.ag.ch/de/smartserviceportal#/',
    },
    {
      title: 'Behördenverzeichnis',
      text: 'Behördenverzeichnis',
      url: 'https://www.ag.ch/de/ueber-uns/kontakt/behoerdenverzeichnis',
    },
  ],
};

interface Props {
  // [1] Used only in isolation but it does not hurt to have it here
  // eslint-disable-next-line react/no-unused-prop-types
  rootElement?: HTMLElement;
  standalone?: boolean;
  swisscoveryUrl?: string;
  translationApiUrl?: string | null;
  translationMissingApiUrl?: string | null;
}

interface I18nProjectConfig {
  ns?: string[];
  backend?: { loadPath: string; addPath?: string };
  saveMissing?: boolean;
}

function App(props: Props) {
  const {
    rootElement,
    standalone,
    swisscoveryUrl,
    translationApiUrl,
    translationMissingApiUrl,
  } = props;

  // Initialize the environment/analytics with the web-config from @ktag/styleguide
  // if you do NOT pass an environmentConfig, the app will try to use the environment
  // config of the 'surrounding' environment, if it is e.g. embedded in www.ag.ch
  environment.initialize(environmentConfig);
  analytics.initialize();

  // initialize i18n and declare all known required namespaces
  // upfront to combine loading labels into a single request.
  const i18nProjectConfig: I18nProjectConfig = {
    ns: ['core', 'swisscoverysearch'],
  };
  if (translationApiUrl) {
    i18nProjectConfig.backend = { loadPath: translationApiUrl };
    if (translationMissingApiUrl) {
      i18nProjectConfig.saveMissing = true;
      i18nProjectConfig.backend.addPath = translationMissingApiUrl;
    }
  }
  i18n.initialize(i18nProjectConfig, {
    plugin: initReactI18next,
    allowMultiLoading: true,
  });

  // NOT STANDALONE (= 'Integrated')
  //
  // Only the actual app is rendered. This will also make the app
  // dependent on CSS and JS from the surrounding web page. Upside is that
  // the look of your app will update with the surrounding page. Downside is
  // that this may break your app. If you want to isolate from outside CSS and
  // JS then provide your own WebWrapper and Stylesheet (see standalone view)
  // and set the app integration to be 'isolated' (see below)
  // if (standalone !== true) {
  //   return (
  //     <Suspense fallback={<Placeholder />}>
  //       <Swisscoverysearch searchUrl={swisscoveryUrl} />
  //     </Suspense>
  //   );
  // }

  // [1] NOT STANDALONE (= 'Integrated'). ISOLATED.
  //
  // Remember to also create a shadow DOM in index.ts
  // A complete web view is rendered around the app including
  // the loading of all necessary resources (css, images, fonts, icons, ...) while
  // outside CSS and JS are mostly ignored. This ensures the app works and looks
  // as expected even if the surrounding website is changed significantly.
  // Important limitations to remember:
  // - JS: Events are bubbling up outside the shadow dom but it will always look as if
  //       the event originated from the shadow root element.
  // - JS: Google reCaptchas and other very complex interactive elements may not work
  //       as expected.
  // - CSS: The :root HTML font size applies for all REM calculations
  // - CSS: Custom webfonts defined inside the shadow DOM are not loaded. But they can be
  //        loaded by the surrounding page and then used inside the shadow DOM.
  if (standalone !== true) {
    return (
      <Suspense fallback={<div />}>
        <WebWrapper resetCSS renderDocument parentElement={rootElement}>
          <Stylesheet inShadowDOM />
          <Swisscoverysearch searchUrl={swisscoveryUrl} />
        </WebWrapper>
      </Suspense>
    );
  }

  // STANDALONE
  //
  // This is the default view in development but apps can provide their own
  // PageWrapper, Header, Footer and overall page layout to create a standlone
  // web app. Because nothing but the react app is rendered, it is no need to
  // be in isolation mode.
  return (
    <Suspense fallback={<div />}>
      <Stylesheet />
      <PageWrapper>
        <Header />
        <PageWrapper.Main>
          <Pagesection>
            <Contentheader
              pagetitle="Kantonsbibliothek"
              printing
              leadtext="Als öffentliche wissenschaftliche Bibliothek vermittelt die Aargauer Kantonsbibliothek Informationen und Wissen für alle."
            />
          </Pagesection>
          <Pagesection type="medium">
            <Suspense fallback={<Placeholder />}>
              <Swisscoverysearch searchUrl={swisscoveryUrl} />
            </Suspense>
          </Pagesection>
          <Pagesection>
            <Relatednav entries={RELATEDNAV_ENTRIES} />
          </Pagesection>
          <Pagesection type="slender">
            <div className="richtext">
              <p>
                Der umfangreiche Bestand der Kantonsbibliothek bietet aktuelle und
                historische Sachbücher, belletristische Werke sowie Spiel- und
                Dokumentarfilme auf DVD. Kundinnen und Kunden haben zudem Zugriff auf die
                digitalen Angebote. Für Forscherinnen und Forscher sind historisch
                bedeutende Sammlungen erschlossen. Und nicht zuletzt ist der renovierte
                klassisch-moderne Bau ein beliebter Arbeits- und Lernort.
              </p>
            </div>
          </Pagesection>
        </PageWrapper.Main>
        <Footer {...FOOTER_CONFIG} />
      </PageWrapper>
    </Suspense>
  );
}

App.defaultProps = {
  standalone: false,
  swisscoveryUrl: '',
  translationApiUrl: null,
  translationMissingApiUrl: null,
};

export default App;
